import { useCallback, useEffect, useState } from "react";
import { CalendarCheck, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { usePoll } from "@/lib/live";
import { fmtDate, ROLE_LABELS } from "@/lib/format";
import { EmptyState } from "@/components/common";
import { PeriodFilter } from "@/components/PeriodFilter";
import { AuthImage } from "@/components/AuthImage";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const Selfie = ({ a }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="rounded-lg overflow-hidden border hover:ring-2 hover:ring-primary" data-testid={`attendance-photo-${a.id}`} aria-label="Lihat selfie">
        <AuthImage path={`/attendance/${a.id}/photo`} alt={a.name} className="h-12 w-12 object-cover" />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="attendance-photo-dialog">
          <DialogHeader><DialogTitle className="font-heading">{a.name} · {fmtDate(a.date)}</DialogTitle></DialogHeader>
          {open && <AuthImage path={`/attendance/${a.id}/photo`} alt={a.name} className="w-full min-h-[240px] rounded-2xl object-contain max-h-[70vh]" />}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default function AbsensiPage() {
  const [range, setRange] = useState(null);
  const [rows, setRows] = useState(null);
  const load = useCallback(() => { if (range) api.get("/attendance", { params: range }).then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e))); }, [range]);
  useEffect(() => { load(); }, [load]);
  usePoll(load);
  const { user } = useAuth();
  const isAdmin = user.role === "admin_cabang";
  const [del, setDel] = useState(null);
  const onDelete = async () => { try { await api.delete(`/attendance/${del.id}`); toast.success("Absensi dihapus"); load(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div>
      <h1 className="font-heading text-2xl sm:text-3xl font-bold mb-5">Absensi</h1>
      <div className="mb-5"><PeriodFilter onChange={setRange} modes={["harian", "bulanan", "tanggal"]} testId="attendance-period" /></div>
      {rows === null ? <p className="text-muted-foreground">Memuat...</p> : rows.length === 0 ? (
        <EmptyState icon={CalendarCheck} title="Belum ada absensi" text="Tidak ada data absensi pada periode ini." testId="attendance-empty" />
      ) : (
        <div className="panel !p-0 overflow-x-auto" data-testid="attendance-table">
          <Table>
            <TableHeader><TableRow><TableHead className="pl-5">Tanggal</TableHead><TableHead>Nama Pegawai</TableHead><TableHead>Jabatan</TableHead><TableHead>Jam Masuk</TableHead><TableHead>Jam Pulang</TableHead><TableHead>Koordinat GPS</TableHead><TableHead>Selfie</TableHead>{isAdmin && <TableHead className="pr-5" />}</TableRow></TableHeader>
            <TableBody>
              {rows.map((a) => (
                <TableRow key={a.id} data-testid={`attendance-row-${a.id}`}>
                  <TableCell className="pl-5 whitespace-nowrap text-sm">{fmtDate(a.date)}</TableCell>
                  <TableCell className="font-medium">{a.name}</TableCell>
                  <TableCell className="text-sm whitespace-nowrap" data-testid={`attendance-role-${a.id}`}>{ROLE_LABELS[a.role || "petugas"]}</TableCell>
                  <TableCell className="font-mono">{a.check_in}</TableCell>
                  <TableCell className="font-mono">{a.check_out || "-"}{a.out_auto ? <span className="text-[10px] text-muted-foreground ml-1">otomatis</span> : null}</TableCell>
                  <TableCell className="font-mono text-xs whitespace-nowrap">{a.lat != null ? <a className="text-primary underline" href={`https://www.google.com/maps?q=${a.lat},${a.lng}`} target="_blank" rel="noreferrer">{a.lat.toFixed(5)}, {a.lng.toFixed(5)}</a> : "-"}</TableCell>
                  <TableCell><Selfie a={a} /></TableCell>
                  {isAdmin && <TableCell className="pr-5"><Button size="sm" variant="ghost" className="h-8 rounded-full text-rose-600" onClick={() => setDel(a)} data-testid={`attendance-delete-${a.id}`}><Trash2 className="h-3.5 w-3.5 mr-1" />Hapus</Button></TableCell>}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <ConfirmDialog open={Boolean(del)} onOpenChange={(o) => !o && setDel(null)} title="Hapus Absensi" danger confirmLabel="Ya, hapus" onConfirm={onDelete}
        description={del && <>Hapus absensi <b>{del.name}</b> tanggal {fmtDate(del.date)}?</>} />
    </div>
  );
}
